import { memo } from 'react';
import { styled } from '@mui/material/styles';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { Chat } from '@trinity/chat';
import { Citation } from '@trinity/chat-components/components/Markdown/Citations';
import { ChatMarkdown } from '../Markdown/Markdown';
import { TableActionsContainer } from './Table.RowContainer';

const ARROW_BUTTON_DIRECTIVE = 'arrowButton';
const INFO_CHIP_DIRECTIVE = 'infoChip';
const CITATION_MARKER_PATTERN = /\^\d+[a-z]*\^/;
const CITATION_VALUE_PATTERN = /^\^([^^]+)\^$/;

type CitationCellProps = {
  value: string;
};

const CitationCell = memo((props: CitationCellProps) => {
  return (
    <Citation
      style={{ maxHeight: 18, minWidth: 12, minHeight: 18 }}
      value={props.value}
    />
  );
});

type ActionMarkdownCellProps = {
  row: Chat.AttachmentTableRow;
  value: string;
};

const ActionMarkdownCell = memo(
  (props: ActionMarkdownCellProps) => {
    return (
      <TableActionsContainer row={props.row}>
        <StyledCell>
          <ChatMarkdown value={props.value} />
        </StyledCell>
      </TableActionsContainer>
    );
  },
  (previousProps, nextProps) => {
    return previousProps.value === nextProps.value && previousProps.row === nextProps.row;
  }
);

const getCellValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  return String(value ?? '');
};

const getCitationValue = (cellValue: string): string | null => {
  if (!CITATION_MARKER_PATTERN.test(cellValue)) {
    return null;
  }

  return cellValue.replace(CITATION_VALUE_PATTERN, '$1');
};

const hasTableActionDirective = (cellValue: string): boolean => {
  return (
    cellValue.includes(ARROW_BUTTON_DIRECTIVE) ||
    cellValue.includes(INFO_CHIP_DIRECTIVE)
  );
};

export const TableCell = (params: GridRenderCellParams<Chat.AttachmentTableRow, unknown>) => {
  const cellValue = getCellValue(params.value);
  const citationValue = getCitationValue(cellValue);

  // Handle citation cells
  if (citationValue !== null) {
    return <CitationCell value={citationValue} />;
  }

  // Handle markdown cells with arrowButton and infoChip directives
  if (hasTableActionDirective(cellValue)) {
    return <ActionMarkdownCell row={params.row} value={cellValue} />;
  }

  // Handle regular cells
  return cellValue;
};

const StyledCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
});
